import React, { FC, useRef, useEffect } from "react";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import { HappyIcon, FormattingIcon } from "../assets/icons/icon-assets";
import { Keyboard } from "react-native";
import { HStack, Text } from "native-base";

interface Props {
  onChange: any;
  onBlur?: () => void;
  type: string;
  label: string;
  defaultValue?: string | undefined;
  width?: any | undefined;
  value: string | undefined;
  height?: any | undefined;
  minRows?: any | undefined;
  multiline?: boolean | undefined;
  maxLength?: any | undefined;
  size?: any | undefined;
  placeholder?: string | undefined;
  autoFocus?: boolean;
  onKeyPress?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  inputRef?: any | null;
  disabled?: boolean;
  shrink?: boolean | true;
  isConvo?: boolean;
  showEmoji?: boolean;
  setShowEmoji?: (show: boolean) => void;
  isMobile?: boolean;
  hasAudio?: boolean;
  isRecording?: boolean;
  recordingTime?: number;
  setRteValue?: any | null;
  useRichText?: boolean;
  setUseRichText?: (show: boolean) => void;
  isItemEdit?: boolean;
  resetAll?: () => void;
  waveformBars?: number[];
}

const AVInputtext: FC<Props> = ({
  onChange,
  onBlur,
  type,
  label,
  defaultValue,
  width,
  value,
  height,
  minRows,
  multiline,
  maxLength,
  size,
  placeholder,
  inputRef,
  onKeyPress,
  disabled,
  shrink,
  isConvo,
  showEmoji,
  setShowEmoji,
  isMobile,
  hasAudio,
  isRecording,
  recordingTime,
  setRteValue,
  useRichText,
  setUseRichText,
  isItemEdit,
  resetAll,
  waveformBars = Array.from({ length: 30 }, () => Math.random() * 12 + 4),
}) => {
  const handleKeyPress = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!multiline) return;

    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      if (onKeyPress) {
        onKeyPress(event);
      }
    }
  };

  const handleEmojiClick = () => {
    if (isMobile) {
      Keyboard.dismiss();
      setTimeout(() => {
        setShowEmoji?.(!showEmoji);
      }, 400);
    } else {
      setShowEmoji?.(!showEmoji);
    }
  };

  const formatTime = (seconds: number): string => {
    if (!seconds || isNaN(seconds) || !isFinite(seconds)) return "00:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  return (
    <TextField
      placeholder={placeholder}
      type={type}
      autoComplete="off"
      inputProps={{
        style: {
          height: height,
          width: width,
        },
        maxLength: maxLength,
      }}
      label={label}
      value={value}
      defaultValue={defaultValue}
      multiline={multiline}
      minRows={minRows}
      onChange={onChange}
      onBlur={onBlur}
      size={size ? size : ""}
      inputRef={inputRef}
      onKeyPress={handleKeyPress}
      disabled={disabled}
      InputLabelProps={{
        shrink: shrink || isRecording,
      }}
      InputProps={
        isConvo
          ? {
              endAdornment: (
                <InputAdornment position="end">
                  {isRecording ? (
                    <HStack
                      space={2}
                      alignItems="center"
                      height="32px"
                      pr={2}
                      width="500px"
                    >
                      <HStack
                        space={0.5}
                        alignItems="center"
                        h="16px"
                        width="60%"
                      >
                        {waveformBars.map((height, i) => (
                          <div
                            key={i}
                            style={{
                              width: "2px",
                              height: `${height}px`,
                              backgroundColor: "#32AE78",
                              borderRadius: "999px",
                              animation: "scaleY 0.5s infinite alternate",
                            }}
                          />
                        ))}
                      </HStack>
                      <Text color="red.500" fontSize="xs">
                        {formatTime(recordingTime || 0)}
                      </Text>
                    </HStack>
                  ) : (
                    !isRecording &&
                    !hasAudio && (
                      <HStack alignItems="center" space={2}>
                        {/* Formatting Icon */}
                        {!isItemEdit && (
                          <div
                            onClick={() => {
                              if (!disabled) {
                                resetAll?.();
                                setRteValue("");
                                setUseRichText?.(!useRichText);
                              }
                            }}
                            style={{
                              cursor: disabled ? "not-allowed" : "pointer",
                              padding: "2px",
                            }}
                          >
                            <FormattingIcon height="20px" width="20px" />
                          </div>
                        )}

                        {/* Emoji Icon */}
                        <div
                          onClick={!disabled ? handleEmojiClick : undefined}
                          style={{
                            cursor: disabled ? "not-allowed" : "pointer",
                            padding: "2px",
                          }}
                        >
                          <HappyIcon size="20px" color="AVColor.primary" />
                        </div>
                      </HStack>
                    )
                  )}
                </InputAdornment>
              ),
            }
          : undefined
      }
      sx={
        isConvo
          ? {
              "& .MuiOutlinedInput-root": {
                borderRadius: "24px",
              },
              "@keyframes scaleY": {
                "0%": {
                  transform: "scaleY(0.5)",
                },
                "100%": {
                  transform: "scaleY(1)",
                },
              },
            }
          : undefined
      }
    />
  );
};

export default AVInputtext;
